import {FC, useCallback} from 'react';
import {styled} from 'styled-components';

import {MEDIA_QUERY_MOBILE_ONLY} from '@shared_web/breakpoints';

import {translate} from '@mavismart_frontend/components/translate';
import {
  BORDER_COLOR,
  LIGHT_GRAY_COLOR,
  MAIN_COLOR,
  TEXT_MAIN_COLOR,
} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';

export type NavMode = 'orbit' | 'pan' | 'zoom';
export type ViewPreset = 'front' | 'back' | 'top' | 'bottom' | 'right' | 'left';

interface NavigationToolbarProps {
  mode: NavMode;
  onModeChange: (mode: NavMode) => void;
  onPresetView: (preset: ViewPreset) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  disabled: boolean;
}

export const NavigationToolbar: FC<NavigationToolbarProps> = props => {
  const {mode, onModeChange, onPresetView, onZoomIn, onZoomOut, disabled} = props;
  const account = useAccount();
  const language = getUserLanguage(account);

  const setOrbit = useCallback(() => onModeChange('orbit'), [onModeChange]);
  const setPan = useCallback(() => onModeChange('pan'), [onModeChange]);
  const setZoom = useCallback(() => onModeChange('zoom'), [onModeChange]);

  const viewFront = useCallback(() => onPresetView('front'), [onPresetView]);
  const viewBack = useCallback(() => onPresetView('back'), [onPresetView]);
  const viewLeft = useCallback(() => onPresetView('left'), [onPresetView]);
  const viewRight = useCallback(() => onPresetView('right'), [onPresetView]);
  const viewTop = useCallback(() => onPresetView('top'), [onPresetView]);
  const viewBottom = useCallback(() => onPresetView('bottom'), [onPresetView]);

  return (
    <Wrapper>
      {/* Navigation mode — desktop only, touch gestures handle this on mobile */}
      <ModeGroup aria-label={translate('door_3d__nav_mode', language)}>
        <ToolButton
          onClick={setOrbit}
          disabled={disabled}
          $active={mode === 'orbit'}
          title={translate('door_3d__nav_orbit', language)}
          aria-pressed={mode === 'orbit'}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12a9 9 0 1 1-6.2-8.6" />
            <polyline points="21 3 21 9 15 9" />
          </svg>
        </ToolButton>
        <ToolButton
          onClick={setPan}
          disabled={disabled}
          $active={mode === 'pan'}
          title={translate('door_3d__nav_pan', language)}
          aria-pressed={mode === 'pan'}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="5 9 2 12 5 15" />
            <polyline points="9 5 12 2 15 5" />
            <polyline points="15 19 12 22 9 19" />
            <polyline points="19 9 22 12 19 15" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <line x1="12" y1="2" x2="12" y2="22" />
          </svg>
        </ToolButton>
        <ToolButton
          onClick={setZoom}
          disabled={disabled}
          $active={mode === 'zoom'}
          title={translate('door_3d__nav_zoom', language)}
          aria-pressed={mode === 'zoom'}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="8" y1="11" x2="14" y2="11" />
            <line x1="11" y1="8" x2="11" y2="14" />
          </svg>
        </ToolButton>
      </ModeGroup>

      <ModeSeparator />

      {/* View presets */}
      <Group aria-label={translate('door_3d__nav_views', language)}>
        <ToolButton
          onClick={viewFront}
          disabled={disabled}
          title={translate('door_3d__view_front', language)}
        >
          <PresetLabel>{translate('door_3d__view_front_short', language)}</PresetLabel>
        </ToolButton>
        <ToolButton
          onClick={viewBack}
          disabled={disabled}
          title={translate('door_3d__view_back', language)}
        >
          <PresetLabel>{translate('door_3d__view_back_short', language)}</PresetLabel>
        </ToolButton>
        <ToolButton
          onClick={viewLeft}
          disabled={disabled}
          title={translate('door_3d__view_left', language)}
        >
          <PresetLabel>{translate('door_3d__view_left_short', language)}</PresetLabel>
        </ToolButton>
        <ToolButton
          onClick={viewRight}
          disabled={disabled}
          title={translate('door_3d__view_right', language)}
        >
          <PresetLabel>{translate('door_3d__view_right_short', language)}</PresetLabel>
        </ToolButton>
        <ToolButton
          onClick={viewTop}
          disabled={disabled}
          title={translate('door_3d__view_top', language)}
        >
          <PresetLabel>{translate('door_3d__view_top_short', language)}</PresetLabel>
        </ToolButton>
        <ToolButton
          onClick={viewBottom}
          disabled={disabled}
          title={translate('door_3d__view_bottom', language)}
        >
          <PresetLabel>{translate('door_3d__view_bottom_short', language)}</PresetLabel>
        </ToolButton>
      </Group>

      <Separator />

      {/* Zoom */}
      <Group aria-label={translate('door_3d__nav_zoom', language)}>
        <ToolButton onClick={onZoomIn} disabled={disabled} title="Zoom +">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </ToolButton>
        <ToolButton onClick={onZoomOut} disabled={disabled} title="Zoom −">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </ToolButton>
      </Group>
    </Wrapper>
  );
};

NavigationToolbar.displayName = 'NavigationToolbar';

const Wrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-radius: 8px;
  background-color: #ffffffdd;
  backdrop-filter: blur(4px);
  border: solid 1px ${LIGHT_GRAY_COLOR};
  z-index: 1;

  ${MEDIA_QUERY_MOBILE_ONLY} {
    position: absolute;
    top: 6px;
    left: 6px;
    right: 6px;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 0;
    padding: 4px;
  }
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  ${MEDIA_QUERY_MOBILE_ONLY} {
    flex-direction: row;
    gap: 0;
  }
`;

const ModeGroup = styled(Group)`
  ${MEDIA_QUERY_MOBILE_ONLY} {
    display: none;
  }
`;

const Separator = styled.div`
  width: 22px;
  height: 1px;
  background-color: ${LIGHT_GRAY_COLOR};
  margin: 2px 0;

  ${MEDIA_QUERY_MOBILE_ONLY} {
    width: 1px;
    height: 22px;
    margin: 0 4px;
  }
`;

const ModeSeparator = styled(Separator)`
  ${MEDIA_QUERY_MOBILE_ONLY} {
    display: none;
  }
`;

const PresetLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  line-height: 1;

  ${MEDIA_QUERY_MOBILE_ONLY} {
    font-size: 10px;
  }
`;

const ToolButton = styled.button<{$active?: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  border: none;
  border-radius: 5px;
  background-color: ${p => (p.$active ? `${MAIN_COLOR}18` : 'transparent')};
  color: ${p => (p.$active ? MAIN_COLOR : TEXT_MAIN_COLOR)};
  cursor: pointer;
  padding: 0 6px;
  font-family: inherit;

  &:hover:not(:disabled) {
    background-color: ${p => (p.$active ? `${MAIN_COLOR}24` : '#00000010')};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  &:focus-visible {
    outline: solid 2px ${BORDER_COLOR};
    outline-offset: -2px;
  }

  ${MEDIA_QUERY_MOBILE_ONLY} {
    min-width: 28px;
    height: 28px;
    padding: 0 5px;
  }
`;
